import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  sendEmailVerification, 
  signInWithPopup, 
  GoogleAuthProvider,
  fetchSignInMethodsForEmail,
  updateProfile,
  signInWithEmailAndPassword
} from 'firebase/auth';
import axios from '../config/axios';
import { ArrowLeft, ArrowRight, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react';
import IATLogoImg from '../assets/iat-logo-3d-brand.png';
import IatCursorGlow from './IatCursorGlow';
import { logger } from '../utils/logger';
import { useCompany } from '../contexts/CompanyContext';
import { isAndroidInstalledApp, STORE_BILLING_MESSAGE } from '../utils/storeEnv';

// ─── Brand tokens ───
const IAT = {
  yellow:     '#FFC107',
  yellowDeep: '#F59E0B',
  graphite:   '#1A1A20',
  charcoal:   '#232329',
  slate:      '#2D2D35',
  iron:       '#5A5A65',
  silver:     '#9A9AA5',
  pewter:     '#C4C4CC',
  cloud:      '#E8E8EC',
  mist:       '#F2F2F4',
  paper:      '#FAFAFB',
  white:      '#FFFFFF',
  ink:        '#0E0E12',
  danger:     '#EF4444',
  borderDark: '#2D2D35',
};
const FONT_SANS    = "'Inter', -apple-system, system-ui, sans-serif";
const FONT_DISPLAY = "'Inter Tight', 'Inter', system-ui, sans-serif";
const FONT_SERIF   = "'Instrument Serif', 'Times New Roman', serif";

const Caption = ({ children, style }) => (
  <div style={{
    fontSize: 11, fontWeight: 600, letterSpacing: '0.12em',
    textTransform: 'uppercase', color: IAT.iron, ...style,
  }}>{children}</div>
);
const fieldShell = (hasError) => ({
  border: `1px solid ${hasError ? IAT.danger : IAT.cloud}`,
  borderRadius: 10,
  padding: '11px 14px',
  background: IAT.white,
  display: 'flex', alignItems: 'center', gap: 10,
});
const inputBase = {
  flex: 1, border: 'none', outline: 'none', background: 'transparent',
  fontFamily: FONT_SANS, fontSize: 14, color: IAT.ink, minWidth: 0,
};
const errorText = { color: IAT.danger, fontSize: 11, marginTop: 4 };

// Função para obter o emoji da bandeira
const getFlagEmoji = (countryCode) => {
  if (!countryCode) return '';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Lista completa de códigos de países para o WhatsApp
const countryCodes = [
  { name: 'Afeganistão', code: '+93', countryCode: 'AF' },
  { name: 'África do Sul', code: '+27', countryCode: 'ZA' },
  { name: 'Albânia', code: '+355', countryCode: 'AL' },
  { name: 'Alemanha', code: '+49', countryCode: 'DE' },
  { name: 'Argélia', code: '+213', countryCode: 'DZ' },
  { name: 'Argentina', code: '+54', countryCode: 'AR' },
  { name: 'Austrália', code: '+61', countryCode: 'AU' },
  { name: 'Áustria', code: '+43', countryCode: 'AT' },
  { name: 'Bélgica', code: '+32', countryCode: 'BE' },
  { name: 'Bolívia', code: '+591', countryCode: 'BO' },
  { name: 'Brasil', code: '+55', countryCode: 'BR' },
  { name: 'Canadá', code: '+1', countryCode: 'CA' },
  { name: 'Chile', code: '+56', countryCode: 'CL' },
  { name: 'China', code: '+86', countryCode: 'CN' },
  { name: 'Colômbia', code: '+57', countryCode: 'CO' },
  { name: 'Coreia do Sul', code: '+82', countryCode: 'KR' },
  { name: 'Dinamarca', code: '+45', countryCode: 'DK' },
  { name: 'Egito', code: '+20', countryCode: 'EG' },
  { name: 'Emirados Árabes Unidos', code: '+971', countryCode: 'AE' },
  { name: 'Espanha', code: '+34', countryCode: 'ES' },
  { name: 'Estados Unidos', code: '+1', countryCode: 'US' },
  { name: 'França', code: '+33', countryCode: 'FR' },
  { name: 'Grécia', code: '+30', countryCode: 'GR' },
  { name: 'Índia', code: '+91', countryCode: 'IN' },
  { name: 'Itália', code: '+39', countryCode: 'IT' },
  { name: 'Japão', code: '+81', countryCode: 'JP' },
  { name: 'México', code: '+52', countryCode: 'MX' },
  { name: 'Portugal', code: '+351', countryCode: 'PT' },
  { name: 'Reino Unido', code: '+44', countryCode: 'GB' },
  { name: 'Rússia', code: '+7', countryCode: 'RU' },
  { name: 'Suíça', code: '+41', countryCode: 'CH' },
  { name: 'Turquia', code: '+90', countryCode: 'TR' },
  { name: 'Uruguai', code: '+598', countryCode: 'UY' },
  { name: 'Venezuela', code: '+58', countryCode: 'VE' },
  // Adicione mais países conforme necessário
];

// Componente SVG do Logo do Google
const GoogleLogo = ({ size = 24 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

// Função de validação de email mais robusta
const validateEmail = (email) => {
  // Esta regex valida:
  // - Parte local com até 64 caracteres, permitindo letras, números, underline e hífen com pontos intercalados
  // - Domínio com pelo menos um ponto e terminação com no mínimo duas letras
  const regex = /^(?=.{1,64}@)[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*(\.[A-Za-z]{2,})$/;
  return regex.test(email);
};

// Funções de validação (CPF, CNPJ, número de telefone e senha)
const validateCPF = (cpf) => {
  cpf = cpf.replace(/[^\d]+/g, '');
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
  let sum = 0;
  let remainder;
  for (let i = 1; i <= 9; i++)
    sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(9, 10))) return false;
  sum = 0;
  for (let i = 1; i <= 10; i++)
    sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(10, 11))) return false;
  return true;
};

const validateCNPJ = (cnpj) => {
  cnpj = cnpj.replace(/[^\d]+/g, '');
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;
  let length = cnpj.length - 2;
  let numbers = cnpj.substring(0, length);
  let digits = cnpj.substring(length);
  let sum = 0;
  let pos = length - 7;
  for (let i = length; i >= 1; i--) {
    sum += numbers.charAt(length - i) * pos--;
    if (pos < 2) pos = 9;
  }
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;
  length += 1;
  numbers = cnpj.substring(0, length);
  sum = 0;
  pos = length - 7;
  for (let i = length; i >= 1; i--) {
    sum += numbers.charAt(length - i) * pos--;
    if (pos < 2) pos = 9;
  }
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;
  return true;
};

const validatePhoneNumber = (number) => {
  const cleanedNumber = number.replace(/\D/g, '');
  return cleanedNumber.length >= 10 && cleanedNumber.length <= 15;
};

const validatePassword = (password) => {
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?#&])[A-Za-z\d@$!%*?#&]{8,}$/;
  return regex.test(password);
};

const RegisterScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateSelectedCompany } = useCompany();
  const auth = getAuth();

  const [isGoogleRegistration, setIsGoogleRegistration] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [cnpjOrCpf, setCnpjOrCpf] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [countryCode, setCountryCode] = useState('+55');
  const [acceptNotifications, setAcceptNotifications] = useState(false);
  const [error, setError] = useState({});
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');

  const dropdownRef = useRef(null);

  // URL para validação do CNPJ/CPF antes do registro (fluxo tradicional)
  const API_BASE = process.env.REACT_APP_API_BASE_URL || axios.defaults.baseURL || '';
  const validateCompanyURL = `${API_BASE}/validate-company`;

  // Captura o parâmetro 'plano' da URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const plano = params.get('plano');
    if (plano === 'atendente' || plano === 'especialista' || plano === 'consultor') {
      setSelectedPlan(plano);
    } else {
      setSelectedPlan('atendente');
    }
  }, [location]);

  // Função para fechar o dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleShowPassword = () => setShowPassword(!showPassword);
  const toggleShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  // Mantemos intencionalmente usuários autenticados na página de registro.
  // Caso desejemos forçar a criação de uma nova conta/empresa, não redirecionamos automaticamente.

  // URLs dos endpoints
  const registerURL = `${API_BASE}/register`;
  const updateClaimsURL = `${API_BASE}/update-claims`;

  // Links de Checkout
  // O checkout agora é criado server-side via /api/checkout-session
  // (Stripe Checkout Session com client_reference_id + metadata.company_id +
  // trial_period_days=7). Os Payment Links estáticos foram aposentados porque
  // não permitiam vincular o Customer Stripe à empresa no Firestore.
  const createCheckoutSessionURL = `${API_BASE}/api/checkout-session`;

  const redirectToCheckout = async (idToken, plan, companyId) => {
    // 🏪 Google Play: sem checkout dentro do app Android (ver storeEnv.js).
    // A conta fica pending_checkout; o usuário finaliza na versão web.
    if (isAndroidInstalledApp()) {
      alert(STORE_BILLING_MESSAGE);
      return;
    }
    const response = await fetch(createCheckoutSessionURL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + idToken,
      },
      body: JSON.stringify({ plan, companyId }),
    });
    const payload = await response.json();
    if (!response.ok || !payload.url) {
      throw new Error(payload.error || 'Falha ao iniciar checkout');
    }
    window.location.assign(payload.url);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError({});
    setMessage('');
    setIsLoading(true);

    const newError = {};

    // Validações dos campos
    if (!fullName || !fullName.trim()) {
      newError.fullName = 'Por favor, insira seu nome.';
    }
    if (!companyName) {
      newError.companyName = 'Por favor, insira o nome da empresa.';
    }
    if (!cnpjOrCpf) {
      newError.cnpjOrCpf = 'Por favor, insira um CPF ou CNPJ.';
    } else {
      const cleanedCnpjOrCpf = cnpjOrCpf.replace(/[^\d]+/g, '');
      if (cleanedCnpjOrCpf.length === 11) {
        if (!validateCPF(cleanedCnpjOrCpf)) {
          newError.cnpjOrCpf = 'CPF inválido. Por favor, insira um CPF válido.';
        }
      } else if (cleanedCnpjOrCpf.length === 14) {
        if (!validateCNPJ(cleanedCnpjOrCpf)) {
          newError.cnpjOrCpf = 'CNPJ inválido. Por favor, insira um CNPJ válido.';
        }
      } else {
        newError.cnpjOrCpf = 'Por favor, insira um CPF ou CNPJ válido com o número correto de dígitos.';
      }
    }
    if (!whatsapp) {
      newError.whatsapp = 'Por favor, insira um número de WhatsApp.';
    } else if (!validatePhoneNumber(whatsapp)) {
      newError.whatsapp = 'Por favor, insira um número de WhatsApp válido.';
    }

    // Validação customizada para email (somente se não for registro via Google)
    if (!isGoogleRegistration) {
      if (!validateEmail(email)) {
        newError.email = 'Por favor, insira um email válido.';
      }
    }

    if (!isGoogleRegistration) {
      if (!validatePassword(password)) {
        newError.password = 'A senha deve conter pelo menos 8 caracteres, incluindo uma letra maiúscula, uma minúscula, um número e um caractere especial.';
      }
      if (password !== confirmPassword) {
        newError.confirmPassword = 'As senhas não coincidem.';
      }
    }

    if (Object.keys(newError).length > 0) {
      setError(newError);
      setIsLoading(false);
      return;
    }

    try {
      if (isGoogleRegistration) {
        // Registro via Google
        await auth.currentUser.reload();
        const user = auth.currentUser;
        const idTokenForRegister = await user.getIdToken(true);

        const response = await fetch(registerURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + idTokenForRegister,
          },
          body: JSON.stringify({
            email: user.email,
            displayName: user.displayName || fullName.trim(),
            companyName,
            cnpjOrCpf,
            whatsapp: `${countryCode}${whatsapp}`,
            acceptNotifications,
            active: true,
            registrationType: "google",
            subscriptionPlan: selectedPlan,
          }),
        });

        const result = await response.json();
        if (!response.ok) {
          setError({ global: result.error || 'Erro ao salvar dados do usuário' });
          throw new Error(result.error || 'Erro ao salvar dados do usuário');
        }

        const idTokenForClaims = await user.getIdToken(true);
        const updateClaimsResponse = await fetch(updateClaimsURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + idTokenForClaims,
          },
          body: JSON.stringify({
            uid: user.uid,
            role: 'admin',
            active: true,
          }),
        });
        if (!updateClaimsResponse.ok) {
          // Não-fatal: o /register já definiu as claims (isAdmin, registrationComplete).
          // Esta reatualização é redundante e pode falhar por propagação do claim de admin
          // recém-criado (verify_admin ainda não vê o claim no token) — não deve travar o cadastro.
          logger.log('Aviso: /update-claims falhou (não-fatal, /register já definiu):', updateClaimsResponse.status);
        }

        await new Promise(resolve => setTimeout(resolve, 2000));
        await auth.currentUser.reload();
        const finalTokenResult = await auth.currentUser.getIdTokenResult(true);
        logger.log("Novo token atualizado:", finalTokenResult.token);

        const checkoutToken = await auth.currentUser.getIdToken(true);
        if (['atendente', 'especialista'].includes((selectedPlan || '').toLowerCase())) {
          // Planos com trial grátis: já está autenticado após o cadastro → entra DIRETO
          // na plataforma (sem passar pela tela de login). Seta a empresa no contexto
          // (igual ao LoginScreen) antes de navegar. Stripe só aparece no fim do trial.
          updateSelectedCompany({ companyId: result.companyId, role: 'admin', companyName });
          navigate(`/settings?companyId=${result.companyId}`, { replace: true });
        } else {
          await redirectToCheckout(checkoutToken, selectedPlan, result.companyId);
        }

      } else {
        // Fluxo tradicional (email/senha)
        const validationResponse = await fetch(validateCompanyURL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cnpjOrCpf: cnpjOrCpf }),
        });
        if (!validationResponse.ok) {
          const validationResult = await validationResponse.json();
          setError({ companyName: validationResult.error || 'Empresa já cadastrada. Por favor, use outro CNPJ/CPF ou faça login.' });
          setIsLoading(false);
          return;
        }

        let user;
        const signInMethods = await fetchSignInMethodsForEmail(auth, email);
        logger.log('Métodos de autenticação para o email:', signInMethods);
        if (signInMethods.length > 0) {
          user = auth.currentUser;
          if (!user) {
            setError({ email: 'Por favor, faça login primeiro.' });
            setIsLoading(false);
            return;
          }
        } else {
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          user = userCredential.user;
          const displayName = fullName.trim();
          await updateProfile(user, { displayName });
          // Planos com trial: o link de verificação já vai DENTRO do e-mail de boas-vindas
          // branded (gerado no backend) — então NÃO mandamos o e-mail genérico do Firebase.
          // Demais planos (consultor): mantém o e-mail de verificação do Firebase.
          if (!['atendente', 'especialista'].includes((selectedPlan || '').toLowerCase())) {
            await sendEmailVerification(user);
          }
        }
        
        const idToken = await user.getIdToken(true);
        
        const response = await fetch(registerURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + idToken,
          },
          body: JSON.stringify({
            email: user.email,
            displayName: user.displayName || fullName.trim(),
            companyName,
            cnpjOrCpf,
            whatsapp: `${countryCode}${whatsapp}`,
            acceptNotifications,
            active: true,
            registrationType: "email",
            subscriptionPlan: selectedPlan,
          }),
        });
        
        const result = await response.json();
        if (!response.ok) {
          if (result.error === 'CPF ou CNPJ inválido') {
            setError({ cnpjOrCpf: 'CPF ou CNPJ inválido. Por favor, verifique e tente novamente.' });
          } else if (result.error === 'Número de WhatsApp inválido') {
            setError({ whatsapp: 'Número de WhatsApp inválido. Por favor, verifique e tente novamente.' });
          } else {
            setError({ global: result.error || 'Erro ao salvar dados do usuário' });
          }
          throw new Error(result.error || 'Erro ao salvar dados do usuário');
        }
        
        const updateClaimsResponse = await fetch(updateClaimsURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + idToken,
          },
          body: JSON.stringify({
            uid: user.uid,
            role: 'admin',
            active: true,
          }),
        });
        logger.log("Resposta da atualização de claims:", updateClaimsResponse.status);
        if (!updateClaimsResponse.ok) {
          // Não-fatal: o /register já definiu as claims (isAdmin, registrationComplete).
          // Esta reatualização é redundante e pode falhar por propagação do claim de admin
          // recém-criado (verify_admin ainda não vê o claim no token) — não deve travar o cadastro.
          logger.log('Aviso: /update-claims falhou (não-fatal, /register já definiu):', updateClaimsResponse.status);
        }

        const checkoutToken = await user.getIdToken(true);
        if (['atendente', 'especialista'].includes((selectedPlan || '').toLowerCase())) {
          // Planos com trial grátis: já está autenticado após o cadastro → entra DIRETO
          // na plataforma (sem passar pela tela de login). Seta a empresa no contexto
          // (igual ao LoginScreen) antes de navegar. Stripe só aparece no fim do trial.
          updateSelectedCompany({ companyId: result.companyId, role: 'admin', companyName });
          navigate(`/settings?companyId=${result.companyId}`, { replace: true });
        } else {
          await redirectToCheckout(checkoutToken, selectedPlan, result.companyId);
        }
      }
    } catch (err) {
      console.error('Erro ao fazer o cadastro:', err);
      
      if (err.code === 'auth/user-token-expired') {
        logger.log('[DEBUG] 🎯 Token expirado (RegisterScreen) - fazendo logout');
        await auth.signOut();
        navigate('/login');
        return;
      } else if (err.code === 'auth/email-already-in-use') {
        // Fluxo de associação caso o e-mail já esteja em uso
        try {
          // Primeiro, tenta fazer login com o email e senha fornecidos
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          const user = userCredential.user;
          
          const idToken = await user.getIdToken();
          const config = { headers: { Authorization: `Bearer ${idToken}` } };
          
          // Agora tenta criar a empresa e associar o usuário
          const response = await fetch(registerURL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + idToken,
            },
            body: JSON.stringify({
              email: user.email,
              displayName: user.displayName || fullName.trim(),
              companyName,
              cnpjOrCpf,
              whatsapp: `${countryCode}${whatsapp}`,
              acceptNotifications,
              active: true,
              registrationType: "email",
              subscriptionPlan: selectedPlan,
            }),
          });
          
          const result = await response.json();
          if (!response.ok) {
            if (result.error === 'CPF ou CNPJ inválido') {
              setError({ cnpjOrCpf: 'CPF ou CNPJ inválido. Por favor, verifique e tente novamente.' });
            } else if (result.error === 'Número de WhatsApp inválido') {
              setError({ whatsapp: 'Número de WhatsApp inválido. Por favor, verifique e tente novamente.' });
            } else {
              setError({ global: result.error || 'Erro ao salvar dados do usuário' });
            }
            throw new Error(result.error || 'Erro ao salvar dados do usuário');
          }
          
          const updateClaimsResponse = await fetch(updateClaimsURL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + idToken,
            },
            body: JSON.stringify({
              uid: user.uid,
              role: 'admin',
              active: true,
            }),
          });
          
          if (!updateClaimsResponse.ok) {
            // Não-fatal: o /register já definiu as claims; reatualização redundante.
            logger.log('Aviso: /update-claims falhou (não-fatal, /register já definiu):', updateClaimsResponse.status);
          }

          const checkoutToken = await user.getIdToken(true);
          if (['atendente', 'especialista'].includes((selectedPlan || '').toLowerCase())) {
            // Planos com trial grátis: já autenticado → entra DIRETO na plataforma.
            updateSelectedCompany({ companyId: result.companyId, role: 'admin', companyName });
            navigate(`/settings?companyId=${result.companyId}`, { replace: true });
          } else {
            await redirectToCheckout(checkoutToken, selectedPlan, result.companyId);
          }

        } catch (err) {
          console.error('Erro ao associar usuário à empresa:', err);
          if (err.code === 'auth/wrong-password') {
            setError({ email: 'Email já cadastrado. Por favor, faça login com sua senha.' });
          } else {
            setError({ global: err.message || 'Erro ao associar usuário à empresa. Por favor, tente novamente.' });
          }
        }
      } else if (err.message && err.message.includes('Empresa já cadastrada')) {
        setError({ companyName: 'Empresa já cadastrada. Por favor, use outro CNPJ/CPF ou faça login.' });
      } else if (err.message && err.message.includes('Unexpected token \'<\'')) {
        setError({ companyName: 'Empresa já cadastrada. Por favor, use outro CNPJ/CPF ou faça login.' });
      } else {
        setError({ global: err.message || 'Erro ao fazer o cadastro. Tente novamente.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'consent'
      });
      const result = await signInWithPopup(auth, provider);
      setIsGoogleRegistration(true);
      setEmail(result.user.email);
    } catch (err) {
      console.error('Erro ao fazer login com Google:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError({ global: 'Registro cancelado pelo usuário.' });
      } else {
        setError({ global: err.message || 'Erro ao fazer login com Google. Tente novamente.' });
      }
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div
      className="iat-register-outer"
      style={{
        // ⚠️ height + overflow:auto — index.css trava html/body/#root.
        // Scroll context próprio aqui. Usa 100dvh (dynamic viewport) que
        // desconta a barra dinâmica do Safari/Chrome mobile — sem isso, o
        // rodapé do form ficava embaixo da barra de UI do iOS.
        height: '100vh',
        background: IAT.graphite, fontFamily: FONT_SANS, color: IAT.paper,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 20, overflow: 'auto',
      }}
    >
      <IatCursorGlow />
      <div
        className="iat-register-shell"
        style={{
          width: '100%', maxWidth: 1200, background: IAT.graphite,
          borderRadius: 18, overflow: 'hidden', border: `1px solid ${IAT.borderDark}`,
          display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.1fr)',
          boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
        }}
      >
        {/* ─── Left: Beny showcase ─── */}
        <div
          className="iat-register-left"
          style={{
            padding: '40px 48px', position: 'relative', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          }}
        >
          <div style={{
            position: 'absolute', left: -100, top: 40, width: 500, height: 500,
            background: `radial-gradient(circle, rgba(255,193,7,0.20) 0%, transparent 60%)`,
            filter: 'blur(80px)', pointerEvents: 'none',
          }}/>
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: `linear-gradient(${IAT.charcoal} 1px, transparent 1px),
                              linear-gradient(90deg, ${IAT.charcoal} 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at 30% 60%, black 0%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse at 30% 60%, black 0%, transparent 70%)',
            opacity: 0.5, pointerEvents: 'none',
          }}/>

          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={IATLogoImg} alt="IAT" height={200} style={{ height: 200, width: 'auto', objectFit: 'contain' }}/>
          </div>

          <div style={{ position: 'relative', marginTop: 48 }}>
            <Caption style={{ color: IAT.yellow, marginBottom: 16 }}>Bem-vindo</Caption>
            <h1 style={{
              fontFamily: FONT_DISPLAY, fontSize: 44, lineHeight: 1.0, letterSpacing: '-0.035em',
              margin: 0, color: IAT.paper, fontWeight: 800,
            }}>
              Crie sua conta.<br/>
              A <span style={{ fontFamily: FONT_SERIF, fontStyle: 'italic', fontWeight: 400, color: IAT.yellow }}>Beny</span> cuida do resto.
            </h1>
            <p style={{ fontSize: 14, lineHeight: 1.55, color: IAT.pewter, marginTop: 16, maxWidth: 400 }}>
              Em poucos minutos sua agente IA está pronta para atender no WhatsApp.
            </p>
          </div>
          {/* Imagem da Beny removida da tela de Registro — mantida só no Login.
              O espaço aqui fica vazio (justifyContent dos elementos acima já
              centraliza naturalmente). */}
        </div>

        {/* ─── Right: Form ─── */}
        <div
          className="iat-register-right"
          style={{
            padding: '40px 48px', background: IAT.white, color: IAT.ink,
            display: 'flex', flexDirection: 'column',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <button
              type="button"
              onClick={handleBackToLogin}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: IAT.iron, fontSize: 13, fontFamily: FONT_SANS, padding: 0,
              }}
              aria-label="Voltar para login"
            >
              <ArrowLeft size={14}/> Voltar
            </button>
            <div style={{ fontSize: 13, color: IAT.iron }}>
              Já tem conta?{' '}
              <button
                type="button"
                onClick={handleBackToLogin}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: IAT.yellowDeep, fontWeight: 600, padding: 0, fontFamily: FONT_SANS, fontSize: 13,
                }}
              >Entrar →</button>
            </div>
          </div>

          <h2 style={{
            fontFamily: FONT_DISPLAY, fontSize: 32, fontWeight: 800, lineHeight: 1.1,
            letterSpacing: '-0.03em', margin: '0 0 6px', color: IAT.ink,
          }}>Criar conta</h2>
          <p style={{ fontSize: 13, color: IAT.iron, margin: '0 0 12px' }}>
            Preencha os dados da empresa para começar.
          </p>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 12, color: IAT.iron, opacity: 0.75,
            background: 'rgba(255,193,7,0.10)',
            border: '1px solid rgba(255,193,7,0.28)',
            borderRadius: 999, padding: '5px 12px', marginBottom: 18,
            fontWeight: 500,
          }}>
            <ArrowRight size={12} style={{ transform: 'rotate(90deg)', color: IAT.yellowDeep }}/>
            Role para preencher todos os campos
          </div>

          {message && (
            <div style={{
              background: 'rgba(16,185,129,0.08)', color: '#047857',
              border: '1px solid rgba(16,185,129,0.22)', borderRadius: 10,
              padding: '10px 12px', fontSize: 13, marginBottom: 12,
            }}>{message}</div>
          )}
          {error.global && (
            <div style={{
              background: 'rgba(239,68,68,0.08)', color: '#B91C1C',
              border: '1px solid rgba(239,68,68,0.22)', borderRadius: 10,
              padding: '10px 12px', fontSize: 13, marginBottom: 12,
            }}>{error.global}</div>
          )}

          <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <input type="hidden" id="selectedPlan" value={selectedPlan} />

            {/* Seu nome */}
            <div>
              <Caption style={{ marginBottom: 6 }}>Seu nome</Caption>
              <div style={fieldShell(!!error.fullName)}>
                <input
                  type="text" id="fullName" value={fullName}
                  onChange={(e) => setFullName(e.target.value)} required
                  style={inputBase} placeholder="Seu nome completo"
                />
              </div>
              {error.fullName && <div style={errorText}>{error.fullName}</div>}
            </div>

            {/* Nome da empresa */}
            <div>
              <Caption style={{ marginBottom: 6 }}>Nome da empresa</Caption>
              <div style={fieldShell(!!error.companyName)}>
                <input
                  type="text" id="companyName" value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)} required
                  style={inputBase} placeholder="Sua empresa"
                />
              </div>
              {error.companyName && <div style={errorText}>{error.companyName}</div>}
            </div>

            {/* CNPJ/CPF */}
            <div>
              <Caption style={{ marginBottom: 6 }}>CNPJ ou CPF</Caption>
              <div style={fieldShell(!!error.cnpjOrCpf)}>
                <input
                  type="text" id="cnpjOrCpf" value={cnpjOrCpf}
                  onChange={(e) => setCnpjOrCpf(e.target.value)} required
                  style={inputBase} placeholder="00.000.000/0000-00"
                />
              </div>
              {error.cnpjOrCpf && <div style={errorText}>{error.cnpjOrCpf}</div>}
            </div>

            {/* WhatsApp */}
            <div>
              <Caption style={{ marginBottom: 6 }}>WhatsApp</Caption>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ position: 'relative', width: 120 }} ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => { setIsDropdownOpen(!isDropdownOpen); setCountrySearch(''); }}
                    style={{
                      width: '100%', padding: '11px 12px',
                      border: `1px solid ${error.whatsapp ? IAT.danger : IAT.cloud}`,
                      borderRadius: 10, background: IAT.white,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      fontFamily: FONT_SANS, fontSize: 14, color: IAT.ink, cursor: 'pointer',
                    }}
                  >
                    <span>
                      {getFlagEmoji(countryCodes.find((c) => c.code === countryCode)?.countryCode)} {countryCode}
                    </span>
                    {isDropdownOpen ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                  </button>
                  {isDropdownOpen && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, minWidth: 300, marginTop: 4,
                      background: IAT.white, border: `1px solid ${IAT.cloud}`, borderRadius: 10,
                      boxShadow: '0 8px 24px rgba(11,11,18,0.12)', overflow: 'hidden',
                      zIndex: 10, fontSize: 13,
                    }}>
                      {/* Busca por nome do país */}
                      <input
                        type="text"
                        autoFocus
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Buscar país..."
                        style={{
                          width: '100%', boxSizing: 'border-box', padding: '10px 12px',
                          border: 'none', borderBottom: `1px solid ${IAT.cloud}`,
                          outline: 'none', fontFamily: FONT_SANS, fontSize: 13, color: IAT.ink,
                        }}
                      />
                      <div style={{ maxHeight: 220, overflow: 'auto' }}>
                        {countryCodes
                          .filter((c) => {
                            const q = countrySearch.trim().toLowerCase();
                            if (!q) return true;
                            return c.name.toLowerCase().includes(q) || c.code.includes(q);
                          })
                          .map((country) => (
                            <div
                              key={`${country.code}-${country.countryCode}`}
                              onClick={() => { setCountryCode(country.code); setIsDropdownOpen(false); setCountrySearch(''); }}
                              style={{
                                cursor: 'pointer', padding: '8px 12px',
                                display: 'flex', alignItems: 'center', gap: 8, color: IAT.ink,
                                transition: 'background 0.15s', whiteSpace: 'nowrap',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = IAT.mist; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                              <span>{getFlagEmoji(country.countryCode)}</span>
                              <span style={{ flex: 1 }}>{country.name}</span>
                              <span style={{ color: IAT.silver, fontSize: 12 }}>{country.code}</span>
                            </div>
                          ))}
                        {countryCodes.filter((c) => {
                          const q = countrySearch.trim().toLowerCase();
                          return !q || c.name.toLowerCase().includes(q) || c.code.includes(q);
                        }).length === 0 && (
                          <div style={{ padding: '10px 12px', color: IAT.silver }}>Nenhum país encontrado</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ ...fieldShell(!!error.whatsapp), flex: 1 }}>
                  <input
                    type="text" id="whatsapp" value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)} required
                    placeholder="DDD + número" style={inputBase}
                  />
                </div>
              </div>
              {error.whatsapp && <div style={errorText}>{error.whatsapp}</div>}
            </div>

            {/* E-mail */}
            {!isGoogleRegistration && (
              <div>
                <Caption style={{ marginBottom: 6 }}>E-mail</Caption>
                <div style={fieldShell(!!error.email)}>
                  <input
                    type="email" id="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} required
                    style={inputBase} placeholder="você@empresa.com.br"
                  />
                </div>
                {error.email && <div style={errorText}>{error.email}</div>}
              </div>
            )}

            {/* Senhas */}
            {!isGoogleRegistration && (
              <>
                <div>
                  <Caption style={{ marginBottom: 6 }}>Senha</Caption>
                  <div style={fieldShell(!!error.password)}>
                    <input
                      type={showPassword ? 'text' : 'password'} id="password" value={password}
                      onChange={(e) => setPassword(e.target.value)} required
                      style={inputBase} placeholder="••••••••••"
                    />
                    <button
                      type="button" onClick={toggleShowPassword}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: IAT.silver, padding: 0, display: 'flex' }}
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
                    </button>
                  </div>
                  {error.password && <div style={errorText}>{error.password}</div>}
                </div>
                <div>
                  <Caption style={{ marginBottom: 6 }}>Confirmar senha</Caption>
                  <div style={fieldShell(!!error.confirmPassword)}>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'} id="confirmPassword" value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)} required
                      style={inputBase} placeholder="••••••••••"
                    />
                    <button
                      type="button" onClick={toggleShowConfirmPassword}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: IAT.silver, padding: 0, display: 'flex' }}
                      aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showConfirmPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
                    </button>
                  </div>
                  {error.confirmPassword && <div style={errorText}>{error.confirmPassword}</div>}
                </div>
              </>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: IAT.iron, cursor: 'pointer' }}>
              <input
                type="checkbox" id="acceptNotifications"
                checked={acceptNotifications}
                onChange={(e) => setAcceptNotifications(e.target.checked)}
                style={{ accentColor: IAT.yellowDeep, width: 16, height: 16 }}
              />
              Aceito receber notificações pelo WhatsApp
            </label>

            <button
              type="submit" disabled={isLoading}
              style={{
                marginTop: 4, padding: '13px 20px',
                background: IAT.yellow, color: IAT.ink,
                border: 'none', borderRadius: 9999, fontSize: 15, fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontFamily: FONT_SANS, letterSpacing: '-0.005em',
                boxShadow: '0 4px 14px rgba(255,193,7,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                opacity: isLoading ? 0.7 : 1,
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              }}
            >
              {isLoading ? 'Cadastrando...' : <>Cadastrar <ArrowRight size={16} style={{ opacity: 0.7 }}/></>}
            </button>
          </form>

          {!isGoogleRegistration && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
                <div style={{ flex: 1, height: 1, background: IAT.cloud }}/>
                <span style={{ fontSize: 11, color: IAT.silver, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase' }}>ou</span>
                <div style={{ flex: 1, height: 1, background: IAT.cloud }}/>
              </div>
              <button
                type="button" onClick={handleGoogleSignIn}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  padding: '12px 16px', background: IAT.white,
                  border: `1px solid ${IAT.cloud}`, borderRadius: 9999,
                  fontSize: 14, fontWeight: 500, color: IAT.ink,
                  cursor: 'pointer', fontFamily: FONT_SANS,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = IAT.paper; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = IAT.white; }}
              >
                <GoogleLogo size={16}/> Registrar com o Google
              </button>
            </>
          )}

          <div style={{ marginTop: 22, textAlign: 'center', fontSize: 11, color: IAT.silver, lineHeight: 1.6 }}>
            <a href="https://www.iatconsulting.com.br/termos-condicoes" target="_blank" rel="noopener noreferrer" style={{ color: IAT.iron, textDecoration: 'underline' }}>
              Termos de uso
            </a>{' | '}
            <a href="https://www.iatconsulting.com.br/politica-privacidade" target="_blank" rel="noopener noreferrer" style={{ color: IAT.iron, textDecoration: 'underline' }}>
              Política de privacidade
            </a>
          </div>
        </div>
      </div>

      <style>{`
        /* Modern browsers (Safari 15.4+, Chrome 108+) — desconta UI bar dinâmica */
        @supports (height: 100dvh) {
          .iat-register-outer { height: 100dvh !important; }
        }
        /* Custom scrollbar — dá pista visual em desktop de que há mais conteúdo */
        .iat-register-outer::-webkit-scrollbar { width: 10px; }
        .iat-register-outer::-webkit-scrollbar-track { background: rgba(255,255,255,0.03); }
        .iat-register-outer::-webkit-scrollbar-thumb {
          background: rgba(255,193,7,0.45); border-radius: 6px;
        }
        .iat-register-outer::-webkit-scrollbar-thumb:hover { background: rgba(255,193,7,0.7); }
        .iat-register-outer { scrollbar-color: rgba(255,193,7,0.45) rgba(255,255,255,0.03); scrollbar-width: thin; }

        /* Tablet/mobile (< 900px) — esconde showcase, só form */
        @media (max-width: 900px) {
          .iat-register-shell {
            grid-template-columns: 1fr !important;
            border-radius: 14px !important;
            background: ${IAT.white} !important;
            border: none !important;
            box-shadow: 0 8px 30px rgba(0,0,0,0.15) !important;
            max-width: 100% !important;
          }
          .iat-register-left { display: none !important; }
          .iat-register-right { padding: 28px 24px !important; }
        }
        /* Mobile — respiro lateral (16px) pro card não colar nas bordas,
           padding-bottom extra pro rodapé ficar acima da UI bar do iOS,
           e box-sizing border-box garante que padding não estoure largura. */
        @media (max-width: 600px) {
          .iat-register-outer { padding: 16px !important; box-sizing: border-box; }
          .iat-register-shell {
            border-radius: 14px !important;
            box-shadow: 0 4px 20px rgba(0,0,0,0.25) !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          .iat-register-right {
            padding: 24px 18px 80px !important;
            box-sizing: border-box !important;
            overflow-x: hidden !important;
          }
          /* Garante que nenhum input/botão estoure horizontalmente */
          .iat-register-right input,
          .iat-register-right button { max-width: 100% !important; box-sizing: border-box !important; }
        }
        /* Mobile pequeno (< 380px) — paddings menores mas mantém respiro */
        @media (max-width: 380px) {
          .iat-register-outer { padding: 10px !important; }
          .iat-register-right { padding: 20px 14px 80px !important; }
        }
      `}</style>
    </div>
  );
};

export default RegisterScreen;